import { Box, Flex, Separator, Text, Callout, Badge } from "@radix-ui/themes";
import { useGondolaChangeGroupsData } from "../hooks/useGondolaChangeGroupsData";
import { useMemo } from "react";
import { ExclamationTriangleIcon } from "@radix-ui/react-icons";
import { selectSelectedPolygon } from "@/entities/annotation";
import { ANNOTATION_ACTIONS } from "@/entities/annotation/model/consts";
import { useAppDispatch, useAppSelector } from "@/app/appStore";
import { InfoItem } from "@/components/info-item";
import { findCamerasWithSkuSection } from "@/features/sku-sections/lib/find-cameras-with-sku";
import { useGondolasSkuSectionsQuery } from "@/shared/api/insights-api";
import { getImageSrc } from "@/shared/api/helpers";
import PolygonCrop from "@/features/sku-sections/components/polygon-crop";
import { displayUUID } from "@/shared";
import {
  selectSelectedSkuSectionChangeId,
  selectSelectedDetectionId,
  setSelectedDetectionId,
  setSelectedSkuSectionChangeId,
  setSelectedTriangulationId,
} from "@/entities/gondola-change-groups/model/slice";
import { Detection, SKUSectionChange } from "@/entities/gondola-change-groups";
import Animal from "react-animals";
import { countAnnotationsType } from "../lib/helpers";

const getBBoxDimensions = (detection: Detection) => {
  const width = Math.trunc(detection.bottomright[0] - detection.topleft[0]);
  const height = Math.trunc(detection.bottomright[1] - detection.topleft[1]);
  return `${width}; ${height}`;
};

const stringifyVector = (vector: [number, number]) =>
  vector.map(Math.trunc).join(", ");

const getBBoxCoordinates = (detection: Detection) => {
  return [
    stringifyVector(detection.topleft),
    stringifyVector(detection.bottomright),
  ].join("; ");
};

const SkuSectionChangeGroupDetails = () => {
  const dispatch = useAppDispatch();
  const selectedOrder = useAppSelector(
    (state) => state.annotation.selectedOrder
  );

  const selectedPolygon = useAppSelector(selectSelectedPolygon);
  const selectedSkuSectionChangeId = useAppSelector(
    selectSelectedSkuSectionChangeId
  );
  const selectedDetectionId = useAppSelector(selectSelectedDetectionId);

  const {
    selectedSkuSectionGroup,
    selectedGondolaChange,
    selectedGondolaChangeGroup,
    selectedSkuSectionChanges,
    findTrackAvatar,
  } = useGondolaChangeGroupsData();

  const { data: skuSectionsData } = useGondolasSkuSectionsQuery(
    {
      gondolaId: String(selectedGondolaChangeGroup?.gondola_id),
    },
    { skip: !selectedGondolaChangeGroup?.gondola_id || !selectedOrder }
  );

  // Check if arrays are similar across all sku_section_changes
  const trackDataValidation = useMemo(() => {
    if (!selectedSkuSectionGroup?.sku_section_changes?.length) {
      return { isValid: true, trackIds: [], startTimes: [], endTimes: [] };
    }

    const changes = selectedSkuSectionGroup.sku_section_changes.filter(
      (change) =>
        change.gondola_change_camera_composite_id ===
          selectedGondolaChange?.gondola_change_camera_composite_id &&
        change.sku_section_id === selectedPolygon?.sku_section_id
    );

    const firstChange = changes?.[0];

    if (firstChange) {
      const referenceTrackIds = [...firstChange.track_ids_in_occlusion];
      const referenceStartTimes = [...firstChange.track_occlusion_start_times];
      const referenceEndTimes = [...firstChange.track_occlusion_end_times];

      const isValid = changes.every((change) => {
        const trackIds = [...change.track_ids_in_occlusion];
        const startTimes = [...change.track_occlusion_start_times];
        const endTimes = [...change.track_occlusion_end_times];

        return (
          JSON.stringify(trackIds) === JSON.stringify(referenceTrackIds) &&
          JSON.stringify(startTimes) === JSON.stringify(referenceStartTimes) &&
          JSON.stringify(endTimes) === JSON.stringify(referenceEndTimes)
        );
      });

      return {
        isValid,
        trackIds: referenceTrackIds,
        startTimes: referenceStartTimes,
        endTimes: referenceEndTimes,
      };
    }

    return { isValid: true, trackIds: [], startTimes: [], endTimes: [] };
  }, [selectedSkuSectionGroup, selectedGondolaChange, selectedPolygon]);

  const camerasWithSelectedSkuSection =
    selectedPolygon && skuSectionsData
      ? findCamerasWithSkuSection(
          selectedPolygon.sku_section_id,
          skuSectionsData
        )
      : null;
  const selectedSkuSection = camerasWithSelectedSkuSection?.find(
    (camera) => camera.cameraId === selectedGondolaChange?.camera_local_id
  )?.skuSection;

  const handleSkuSectionChangeClick = (change: SKUSectionChange) => {
    const detectionId = change.detection?.id
      ? String(change.detection?.id)
      : change.triangulation?.triangulation_id;

    if (
      selectedSkuSectionChangeId ===
        change.gondola_change_camera_composite_id &&
      selectedDetectionId === detectionId
    ) {
      dispatch(setSelectedSkuSectionChangeId(null));
      dispatch(setSelectedDetectionId(null));
      dispatch(setSelectedTriangulationId(null));
      return;
    }

    if (change) {
      dispatch(
        setSelectedSkuSectionChangeId(change.gondola_change_camera_composite_id)
      );
      dispatch(
        setSelectedDetectionId(
          change.detection?.id?.toString() ||
            change.triangulation?.triangulation_id ||
            null
        )
      );
      dispatch(
        setSelectedTriangulationId(
          change.triangulation?.triangulation_id || null
        )
      );
    }
  };

  const transactionAnnotationsBalance = useMemo(() => {
    if (!selectedSkuSectionGroup?.annotations?.length) {
      return 0;
    }

    return selectedSkuSectionGroup.annotations.reduce((balance, annotation) => {
      const countValue =
        annotation.update_type === ANNOTATION_ACTIONS.ADD
          ? annotation.count
          : -annotation.count;
      return balance + countValue;
    }, 0);
  }, [selectedSkuSectionGroup?.annotations]);

  const totalNumberOfTakeAnnotations = useMemo(() => {
    return countAnnotationsType(
      selectedSkuSectionGroup?.annotations || [],
      ANNOTATION_ACTIONS.ADD
    );
  }, [selectedSkuSectionGroup?.annotations]);

  const totalNumberOfPutAnnotations = useMemo(() => {
    return countAnnotationsType(
      selectedSkuSectionGroup?.annotations || [],
      ANNOTATION_ACTIONS.REMOVE
    );
  }, [selectedSkuSectionGroup?.annotations]);

  const hasDetections = selectedSkuSectionChanges.some(
    (change) => change.detection
  );

  const hasPositiveProximityAffinityScore = useMemo(() => {
    return (
      selectedSkuSectionGroup?.proxs_match_scores.some((score) => score > 0) ||
      false
    );
  }, [selectedSkuSectionGroup?.proxs_match_scores]);

  const getTotalPredictionBalanceDisplay = () => {
    const value = selectedSkuSectionGroup?.total_update_quantity;
    const isDifferent = value !== transactionAnnotationsBalance;

    if (isDifferent) {
      let displayValue;
      if (value === null) {
        displayValue = "-";
      } else if (value === undefined) {
        displayValue = "undefined";
      } else {
        displayValue = value;
      }

      return <Badge color="red">{displayValue}</Badge>;
    }

    return value;
  };

  if (!selectedSkuSectionChanges.length) {
    return null;
  }

  return (
    <Box mt="4">
      <Separator size="4" />
      <Flex align="center" justify="between" m="2">
        <Text size="2" weight="bold" as="p">
          SKU Section Change Group Details
        </Text>
      </Flex>
      <Separator size="4" />
      <Flex flexBasis="1" flexGrow="1" gap="2" p="2">
        <Flex
          direction="column"
          flexGrow="1"
          flexBasis="1"
          flexShrink="0"
          align="center"
          justify="between"
        >
          <Text size="1" color="gray" align="center">
            Is Accepted:
          </Text>
          <Text size="2">
            {selectedSkuSectionGroup?.is_accepted ? "✅" : "❌"}
          </Text>
        </Flex>
        <Flex
          direction="column"
          flexGrow="1"
          flexBasis="1"
          flexShrink="0"
          align="center"
          justify="between"
        >
          <Text size="1" color="gray" align="center">
            Has Annotation:
          </Text>
          <Text size="2">
            {selectedSkuSectionGroup?.has_annotation ? "✅" : "❌"}
          </Text>
        </Flex>
        <Flex
          direction="column"
          flexGrow="1"
          flexBasis="1"
          align="center"
          justify="between"
        >
          <Text size="1" color="gray" align="center">
            Has Time Intersected Proximity:
          </Text>
          <Text size="2">
            {selectedSkuSectionGroup &&
            selectedSkuSectionGroup.intersected_proxs_count > 0
              ? "✅"
              : "❌"}
          </Text>
        </Flex>
        <Flex
          direction="column"
          flexGrow="1"
          flexBasis="1"
          align="center"
          justify="between"
        >
          <Text size="1" color="gray" align="center">
            Has Positive Proximity Affinity Score:
          </Text>
          <Text size="2">
            {hasPositiveProximityAffinityScore ? "✅" : "❌"}
          </Text>
        </Flex>
      </Flex>

      <Flex flexBasis="1" flexGrow="1" p="2">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className="px-1 py-1 text-center text-xs font-normal text-gray-500">
                Track ID:
              </th>
              <th className="px-1 py-1 text-left text-xs font-normal text-gray-500">
                Occlusion start:
              </th>
              <th className="px-1 py-1 text-left text-xs font-normal text-gray-500">
                Occlusion end:
              </th>
            </tr>
          </thead>
          <tbody>
            {trackDataValidation.trackIds.map((trackId, index) => {
              const avatarBinding = findTrackAvatar(trackId);
              const startTime = trackDataValidation.startTimes[index];
              const endTime = trackDataValidation.endTimes[index];

              return (
                <tr key={trackId} className="h-[24px]">
                  <td className="px-1 py-1">
                    <div className="flex justify-center items-center">
                      {avatarBinding ? (
                        <Animal
                          name={avatarBinding.animalName}
                          color={avatarBinding.color}
                          size="16px"
                        />
                      ) : null}
                    </div>
                  </td>
                  <td className="px-1 py-1 text-xs text-left">{startTime}</td>
                  <td className="px-1 py-1 text-xs text-left">{endTime}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Flex>
      <Box px="4">
        <InfoItem
          label="Intersected Proxs Unique Tracks Count"
          value={selectedSkuSectionGroup?.intersected_proxs_unique_tracks_count}
        />
        <InfoItem
          label="Intersected Proxs Count"
          value={selectedSkuSectionGroup?.intersected_proxs_count}
        />
        <InfoItem
          label="TAKE Annotations Count"
          value={totalNumberOfTakeAnnotations}
        />
        <InfoItem
          label="PUT Annotations Count"
          value={totalNumberOfPutAnnotations}
        />
        <InfoItem
          label="Total Transaction Annotations Balance"
          value={transactionAnnotationsBalance}
        />
        <InfoItem
          label="Total Prediction Balance"
          customValue={getTotalPredictionBalanceDisplay()}
        />

        <InfoItem
          label="Total Journeys Update Balance"
          value={selectedSkuSectionGroup?.total_journeys_update_quantity}
        />
      </Box>

      <Text size="2" weight="bold" as="p" mt="4" ml="4">
        {hasDetections
          ? `Detections (${selectedSkuSectionChanges.length})`
          : "No Detection"}
      </Text>

      <Box py="2" px="4">
        {hasDetections &&
          selectedSkuSectionChanges.map((change, index) => {
            const detectionId = change.detection?.id
              ? String(change.detection?.id)
              : change.triangulation?.triangulation_id;

            const isSelected =
              selectedSkuSectionChangeId ===
                change.gondola_change_camera_composite_id &&
              selectedDetectionId === detectionId;

            return (
              <Flex
                key={index}
                align="center"
                gap="2"
                className={`p-2 rounded  ${
                  isSelected
                    ? "bg-blue-100 border border-blue-300"
                    : "hover:bg-gray-50"
                }`}
                onClick={() => {
                  handleSkuSectionChangeClick(change);
                }}
              >
                <Box height="110px" width="110px">
                  {selectedSkuSection ? (
                    <PolygonCrop
                      imageUrl={getImageSrc(selectedSkuSection?.original_image)}
                      polygonPoints={selectedSkuSection?.polygon_points}
                      originalWidth={selectedSkuSection?.width}
                      originalHeight={selectedSkuSection?.height}
                    />
                  ) : (
                    <Flex
                      flexBasis="1"
                      flexGrow="1"
                      justify="center"
                      align="center"
                      className=" bg-gray-200 rounded-md"
                    >
                      <Text size="1" color="gray">
                        Camera image doesn't exist
                      </Text>
                    </Flex>
                  )}
                </Box>
                <Box>
                  <InfoItem
                    label="Action"
                    value={change.detection ? change.detection?.action : "❌"}
                  />
                  <InfoItem
                    label="isTriangulated"
                    value={change.triangulation ? "✅" : "❌"}
                  />
                  <InfoItem
                    label="Detection Confidence"
                    value={
                      change.detection
                        ? Math.trunc(change.detection?.bbox_conf * 100) / 100
                        : "❌"
                    }
                  />
                  {change.detection && (
                    <>
                      <InfoItem
                        label="BBox width & height (px)"
                        value={getBBoxDimensions(change.detection)}
                      />
                      <InfoItem
                        label="Coordinates TL & BR (px)"
                        value={getBBoxCoordinates(change.detection)}
                      />
                    </>
                  )}
                  <InfoItem
                    label="Triangulation Confidence"
                    value={
                      change.triangulation
                        ? change.triangulation?.point3d_confidence
                            ?.map((conf) => Math.trunc(conf * 100) / 100)
                            .join(", ")
                        : "❌"
                    }
                  />
                  <InfoItem
                    label="Triangulation ID"
                    copyValue={change.triangulation?.triangulation_id}
                    value={
                      change.triangulation?.triangulation_id
                        ? displayUUID(change.triangulation?.triangulation_id)
                        : "❌"
                    }
                  />
                </Box>
              </Flex>
            );
          })}
      </Box>

      {!trackDataValidation.isValid && (
        <Box p="2">
          <Callout.Root color="red">
            <Callout.Icon>
              <ExclamationTriangleIcon />
            </Callout.Icon>
            <Callout.Text>
              There are dissimilar Track IDs or Track Occlusion start or end
              times across SKU section changes.
            </Callout.Text>
          </Callout.Root>
        </Box>
      )}
    </Box>
  );
};

export default SkuSectionChangeGroupDetails;
